import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';

// Agent context types
const AgentActions = {
  INITIALIZE_AGENT: 'INITIALIZE_AGENT',
  SPAWN_WIDGET: 'SPAWN_WIDGET',
  UPDATE_CONTEXT: 'UPDATE_CONTEXT',
  CLOSE_WIDGET: 'CLOSE_WIDGET',
  SET_ACTIVE_AGENT: 'SET_ACTIVE_AGENT',
  CLEAR_AGENTS: 'CLEAR_AGENTS',
  // Multi-agent actions
  SET_CONVERSATION: 'SET_CONVERSATION',
  UPDATE_AGENT_INFO: 'UPDATE_AGENT_INFO',
  SET_WELCOME_MESSAGE: 'SET_WELCOME_MESSAGE'
};

// GraphQL queries for agent interactions
const AGENT_CONTEXT_QUERY = gql`
  query GetAgentContext($pageContext: String!, $userContext: String!) {
    agentContext(pageContext: $pageContext, userContext: $userContext) {
      availableAgents
      contextData
      suggestions
    }
  }
`;

const AGENT_INTERACTION_MUTATION = gql`
  mutation InteractWithAgent($agentType: String!, $message: String!, $conversationId: String, $context: JSON!, $currentTeaser: JSON, $editingField: String) {
    interactWithAgent(agentType: $agentType, message: $message, conversationId: $conversationId, context: $context, currentTeaser: $currentTeaser, editingField: $editingField) {
      response
      conversationId
      activeAgentType
      availableAgents
      agentSwitched
      widgetData
      widgetType
      widgetProps
      nextActions
      learningTriggered
      readyToGenerate
    }
  }
`;

const SWITCH_AGENT_MUTATION = gql`
  mutation SwitchAgent($conversationId: String!, $targetAgentType: String!, $reason: String) {
    switchAgent(conversationId: $conversationId, targetAgentType: $targetAgentType, reason: $reason) {
      success
      activeAgentType
      conversationId
      availableAgents
      error
    }
  }
`;

const START_CONVERSATION_MUTATION = gql`
  mutation StartConversation($agentType: String!, $context: JSON!) {
    startConversation(agentType: $agentType, context: $context) {
      conversationId
      activeAgentType
      availableAgents
      welcomeMessage
      success
      error
    }
  }
`;

// Initial state for agent system
const initialState = {
  activeAgents: {},
  spawnedWidgets: [],
  currentContext: {},
  isLoading: false,
  error: null,
  // Multi-agent support
  activeAgentType: null,
  availableAgents: [],
  conversationId: null,
  welcomeMessage: null
};

// Agent reducer
function agentReducer(state, action) {
  switch (action.type) {
    case AgentActions.INITIALIZE_AGENT:
      return {
        ...state,
        activeAgents: {
          ...state.activeAgents,
          [action.payload.agentId]: action.payload.agent
        }
      };
    
    case AgentActions.SPAWN_WIDGET:
      return {
        ...state,
        spawnedWidgets: [
          ...state.spawnedWidgets,
          {
            id: action.payload.widgetId,
            type: action.payload.widgetType,
            props: action.payload.widgetProps,
            position: action.payload.position,
            context: action.payload.context,
            agentId: action.payload.agentId,
            timestamp: Date.now()
          }
        ]
      };
    
    case AgentActions.UPDATE_CONTEXT:
      return {
        ...state,
        currentContext: {
          ...state.currentContext,
          ...action.payload
        }
      };
    
    case AgentActions.CLOSE_WIDGET:
      return {
        ...state,
        spawnedWidgets: state.spawnedWidgets.filter(
          widget => widget.id !== action.payload.widgetId
        )
      };
    
    case AgentActions.SET_ACTIVE_AGENT:
      return {
        ...state,
        activeAgent: action.payload.agentId
      };
    
    case AgentActions.CLEAR_AGENTS:
      return {
        ...state,
        activeAgents: {},
        spawnedWidgets: [],
        activeAgent: null
      };

    case AgentActions.SET_CONVERSATION:
      return {
        ...state,
        conversationId: action.payload.conversationId,
        activeAgentType: action.payload.activeAgentType,
        availableAgents: action.payload.availableAgents || [],
        welcomeMessage: action.payload.welcomeMessage
      };

    case AgentActions.UPDATE_AGENT_INFO:
      return {
        ...state,
        activeAgentType: action.payload.activeAgentType || state.activeAgentType,
        availableAgents: action.payload.availableAgents || state.availableAgents
      };

    case AgentActions.SET_WELCOME_MESSAGE:
      return {
        ...state,
        welcomeMessage: action.payload.welcomeMessage
      };

    default:
      return state;
  }
}

// Agent context
const AgentContext = createContext();

// Agent provider component
export const AgentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(agentReducer, initialState);
  
  // Safely get Apollo Client - might not be available
  let apolloClient;
  try {
    apolloClient = useApolloClient();
  } catch (error) {
    console.warn('Apollo Client not available in AgentProvider:', error.message);
    apolloClient = null;
  }

  // Update context based on current page/component
  const updateContext = (contextData) => {
    dispatch({
      type: AgentActions.UPDATE_CONTEXT,
      payload: contextData
    });
  };

  // Initialize agent based on context
  const initializeAgent = async (agentType, context = {}) => {
    try {
      const agentId = `${agentType}_${Date.now()}`;
      
      if (apolloClient) {
        // Query backend for agent initialization
        const { data } = await apolloClient.query({
          query: AGENT_CONTEXT_QUERY,
          variables: {
            pageContext: context.page || 'succession_dashboard',
            userContext: JSON.stringify(context.user || {})
          }
        });

        dispatch({
          type: AgentActions.INITIALIZE_AGENT,
          payload: {
            agentId,
            agent: {
              type: agentType,
              context: data?.agentContext || {},
              initialized: true
            }
          }
        });
      } else {
        // Fallback initialization without Apollo Client
        dispatch({
          type: AgentActions.INITIALIZE_AGENT,
          payload: {
            agentId,
            agent: {
              type: agentType,
              context: context,
              initialized: true
            }
          }
        });
      }

      return agentId;
    } catch (error) {
      console.error('Agent initialization failed:', error);
      return null;
    }
  };

  // Spawn a widget with contextual data
  const spawnWidget = (widgetType, props = {}, position = 'bottom-right', agentId = null) => {
    const widgetId = `widget_${Date.now()}`;
    
    dispatch({
      type: AgentActions.SPAWN_WIDGET,
      payload: {
        widgetId,
        widgetType,
        widgetProps: props,
        position,
        context: state.currentContext,
        agentId
      }
    });

    return widgetId;
  };

  // Interact with agent
  const interactWithAgent = async (agentId, message, additionalContext = {}) => {
    try {
      const agent = state.activeAgents[agentId];
      if (!agent) return null;

      if (apolloClient) {
        // Extract currentTeaser and editingField from additionalContext if present
        const { currentTeaser, editingField, ...contextWithoutTeaser } = additionalContext;

        const { data } = await apolloClient.mutate({
          mutation: AGENT_INTERACTION_MUTATION,
          variables: {
            agentType: agent.type,
            message,
            conversationId: additionalContext.session_id || state.conversationId || null,
            context: {
              ...state.currentContext,
              ...contextWithoutTeaser,
              agentId
            },
            currentTeaser: currentTeaser || null,
            editingField: editingField || null
          }
        });

        const interaction = data?.interactWithAgent;

        // Update conversation info if returned
        if (interaction?.conversationId && interaction.conversationId !== state.conversationId) {
          dispatch({
            type: AgentActions.SET_CONVERSATION,
            payload: {
              conversationId: interaction.conversationId,
              activeAgentType: interaction.activeAgentType || state.activeAgentType,
              availableAgents: interaction.availableAgents || state.availableAgents
            }
          });
        }

        // Handle automatic agent switches
        if (interaction?.agentSwitched && interaction.activeAgentType) {
          dispatch({
            type: AgentActions.UPDATE_AGENT_INFO,
            payload: {
              activeAgentType: interaction.activeAgentType,
              availableAgents: interaction.availableAgents || state.availableAgents
            }
          });
        }

        // If agent wants to spawn a widget, do it
        if (interaction?.widgetType) {
          spawnWidget(
            interaction.widgetType,
            interaction.widgetProps,
            'contextual',
            agentId
          );
        }

        return interaction;
      } else {
        // Return null so fallback processing can be used
        console.warn('Apollo Client not available - interaction will use fallback processing');
        return null;
      }
    } catch (error) {
      console.error('Agent interaction failed:', error);
      return null;
    }
  };

  // Close widget
  const closeWidget = (widgetId) => {
    dispatch({
      type: AgentActions.CLOSE_WIDGET,
      payload: { widgetId }
    });
  };

  // Set active agent
  const setActiveAgent = (agentId) => {
    dispatch({
      type: AgentActions.SET_ACTIVE_AGENT,
      payload: { agentId }
    });
  };

  // Clear all agents and widgets
  const clearAgents = () => {
    dispatch({ type: AgentActions.CLEAR_AGENTS });
  };

  // Start a new agent conversation (multi-agent support)
  const startConversation = async (agentType, context = {}) => {
    if (!apolloClient) {
      console.warn('Apollo Client not available - cannot start conversation');
      return null;
    }

    try {
      const { data } = await apolloClient.mutate({
        mutation: START_CONVERSATION_MUTATION,
        variables: {
          agentType,
          context: {
            ...state.currentContext,
            ...context
          }
        }
      });

      if (data?.startConversation) {
        const result = data.startConversation;

        dispatch({
          type: AgentActions.SET_CONVERSATION,
          payload: {
            conversationId: result.conversationId,
            activeAgentType: result.activeAgentType,
            availableAgents: result.availableAgents || []
          }
        });

        return result;
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
      return null;
    }
  };

  // Switch to a different agent (multi-agent support)
  const switchAgent = async (targetAgentType, reason = '') => {
    if (!apolloClient) {
      console.warn('Apollo Client not available - cannot switch agent');
      return { success: false, error: 'Apollo Client not available' };
    }

    if (!state.conversationId) {
      console.warn('No active conversation - starting new conversation with agent');
      return await startConversation(targetAgentType);
    }

    try {
      const { data } = await apolloClient.mutate({
        mutation: SWITCH_AGENT_MUTATION,
        variables: {
          conversationId: state.conversationId,
          targetAgentType,
          reason
        }
      });

      if (data?.switchAgent) {
        const result = data.switchAgent;

        if (result.success) {
          dispatch({
            type: AgentActions.UPDATE_AGENT_INFO,
            payload: {
              activeAgentType: result.activeAgentType,
              availableAgents: result.availableAgents || state.availableAgents
            }
          });
        }

        return result;
      }
    } catch (error) {
      console.error('Failed to switch agent:', error);
      return {
        success: false,
        error: error.message || 'Failed to switch agent'
      };
    }
  };

  // Context value
  const contextValue = {
    ...state,
    updateContext,
    initializeAgent,
    spawnWidget,
    interactWithAgent,
    closeWidget,
    setActiveAgent,
    clearAgents,
    // Multi-agent functions
    startConversation,
    switchAgent
  };

  return (
    <AgentContext.Provider value={contextValue}>
      {children}
    </AgentContext.Provider>
  );
};

// Hook to use agent context
export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};

// Hook for context-aware agent initialization
export const useContextAgent = (agentType, dependencies = []) => {
  const { initializeAgent, updateContext, currentContext } = useAgent();

  useEffect(() => {
    const detectContext = () => {
      // Auto-detect context based on current component/page
      const pageContext = {
        url: window.location.pathname,
        component: 'succession_dashboard', // This would be dynamic
        timestamp: Date.now()
      };
      
      updateContext(pageContext);
      
      // Initialize agent with detected context
      if (agentType) {
        initializeAgent(agentType, pageContext);
      }
    };

    detectContext();
  }, [agentType, ...dependencies]);

  return currentContext;
};

export default AgentProvider;